import React, { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../../components/button';
import { Card, CardBody, CardHeader } from '../../components/ui';
import { HomePageSettings, HomePageTextField, HomeWords } from '../../utils/homePageSettings';
import { HomeTextField } from './HomeTextField';
import { HomePagePatch, buildTextPatch, canSaveText, initialTextForm } from './pageSettingsForm';

const Fields = styled.div`
  ${tw`flex flex-col gap-4`}
`;

const Actions = styled.div`
  ${tw`flex flex-wrap gap-2 mt-4`}
`;

const Note = styled.p`
  ${tw`mt-3 mb-0 text-sm`}
  color: var(--color-text-secondary);
`;

export type HomeWordsField = { field: HomePageTextField; label: string; multiline?: boolean };

/** "Website headline" and "Mobile app home": two words boxes and one Save (spec 029). */
export function HomeWordsCard({
  title,
  description,
  fields,
  saved,
  defaults,
  saving,
  serverErrors,
  note,
  renderPreview,
  testId,
  onSave,
}: {
  title: string;
  description: string;
  fields: HomeWordsField[];
  saved: HomePageSettings;
  defaults: HomeWords;
  saving: boolean;
  serverErrors: Record<string, string> | null;
  note?: ReactNode;
  renderPreview?: (form: Partial<Record<HomePageTextField, string>>) => ReactNode;
  testId: string;
  onSave: (patch: HomePagePatch) => Promise<void>;
}) {
  const names = fields.map(f => f.field);
  const savedKey = names.map(n => saved[n] ?? '').join('\u0001');
  const [form, setForm] = useState(() => initialTextForm(names, saved, defaults));

  // A save (or another admin's change after a reload) resets the boxes.
  useEffect(() => {
    setForm(initialTextForm(names, saved, defaults));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  const canSave = canSaveText(names, form, saved, defaults) && !saving;

  return (
    <Card data-testid={testId}>
      <CardHeader title={title} description={description} />
      <CardBody>
        <Fields>
          {fields.map(f => (
            <HomeTextField
              key={f.field}
              field={f.field}
              label={f.label}
              multiline={f.multiline}
              value={form[f.field] ?? ''}
              defaults={defaults}
              serverError={serverErrors?.[f.field]}
              disabled={saving}
              onChange={value => setForm(prev => ({ ...prev, [f.field]: value }))}
            />
          ))}
        </Fields>
        {renderPreview?.(form)}
        {note && <Note>{note}</Note>}
        <Actions>
          <Button
            text={saving ? 'Saving…' : 'Save'}
            type="button"
            onClick={() => onSave(buildTextPatch(names, form, saved, defaults))}
            isLoading={saving}
            isDisabled={!canSave}
            dataTestId={`${testId}-save`}
          />
        </Actions>
      </CardBody>
    </Card>
  );
}

const Phone = styled.div`
  ${tw`mt-5 overflow-hidden`}
  width: 100%;
  max-width: 320px;
  border-radius: 28px;
  border: 6px solid #111;
  background: linear-gradient(160deg, var(--color-black) 0%, #3a3f4b 100%);
  padding: 28px 20px 36px;
`;

const PhoneTitle = styled.p`
  ${tw`m-0`}
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 32px;
  line-height: 1.15;
  color: #fff;
  overflow-wrap: anywhere;
`;

const PhoneSubtitle = styled.p`
  ${tw`mt-2 mb-0`}
  font-size: 16px;
  line-height: 1.25;
  color: rgba(255, 255, 255, 0.95);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

const PreviewLabel = styled.p`
  ${tw`mt-5 mb-0 text-xs font-medium uppercase`}
  letter-spacing: 0.04em;
  color: var(--color-text-secondary);
`;

/** A sketch of the app's home header: same sizes and the 3-line cut as `HomeHero`. */
export function PhonePreview({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <>
      <PreviewLabel>Preview</PreviewLabel>
      <Phone role="img" aria-label="App home preview" data-testid="app-home-preview">
        <PhoneTitle>{title}</PhoneTitle>
        <PhoneSubtitle>{subtitle}</PhoneSubtitle>
      </Phone>
    </>
  );
}
